import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm, type ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createVehicle, editVehicle, VEHICLES_QUERY_KEY } from './VehicleManager.service'
import { type CreateOrEditVehicleFormValues, FuelType, type VehicleData, VehicleType } from './VehicleManager.types'

interface CreateOrEditVehicleDialogPayload {
  vehicle: VehicleData
}

interface CreateOrEditVehicleDialogProps {
  payload?: CreateOrEditVehicleDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const CreateOrEditVehicleDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditVehicleDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<CreateOrEditVehicleFormValues>()
  const queryClient = useQueryClient()
  const createVehicleMutation = useMutation({
    mutationFn: createVehicle,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: [VEHICLES_QUERY_KEY] }),
  })
  const updateVehicleMutation = useMutation({
    mutationFn: editVehicle,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: [VEHICLES_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          licensePlate: payload?.vehicle.licensePlate,
          vehicleType: payload?.vehicle.vehicleType,
          weightCapacity: payload?.vehicle.weightCapacity,
          fuelType: payload?.vehicle.fuelType,
          makeModel: payload?.vehicle.makeModel,
          assetId: payload?.vehicle.assetId,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<CreateOrEditVehicleFormValues> = async (data: CreateOrEditVehicleFormValues) =>
    handleFormSubmission(async () => {
      if (payload) {
        await updateVehicleMutation.mutateAsync({
          licensePlate: payload.vehicle.licensePlate,
          vehicleType: payload.vehicle.vehicleType,
          data,
        })
      } else {
        await createVehicleMutation.mutateAsync(data)
      }
    }, {
      onSuccess: () => {
        analyticsEvent(payload ? 'polestar_cs_vehicle_updated' : 'polestar_cs_vehicle_created')
        onAction()
      },
      onError: (error) => {
        setError('root', processServerErrorMessages(error))
      },
    })

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={
        payload
          ? `${payload.vehicle.licensePlate} — ${t('screens.cs.vehicle_manager.card.actions.edit')}`
          : t('screens.cs.vehicle_manager.card.actions.new')
      }
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {payload
            ? t('screens.cs.vehicle_manager.add_edit_vehicle.update')
            : t('screens.cs.vehicle_manager.add_edit_vehicle.create')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormText
            name="licensePlate"
            label={t('screens.cs.vehicle_manager.add_edit_vehicle.license_plate')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
            disabled={!!payload}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormDropdown
            name="vehicleType"
            label={t('screens.cs.vehicle_manager.add_edit_vehicle.vehicle_type')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
            options={Object.values(VehicleType).map((type) => ({
              value: type,
              label: t(`screens.cs.vehicle_manager.vehicle_types.${type}`),
            }))}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <KNFormText
            name="weightCapacity"
            label={t('screens.cs.vehicle_manager.add_edit_vehicle.weight_capacity')}
            control={control}
            rules={{
              validate: {
                isNumber: (value): ValidateResult => {
                  if (value && isNaN(Number(value))) {
                    return t('form.validation.number')
                  }
                  return true
                },
              },
            }}
            endAdornment="kg"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <KNFormDropdown
            name="fuelType"
            label={t('screens.cs.vehicle_manager.add_edit_vehicle.fuel_type')}
            control={control}
            options={Object.values(FuelType).map((type) => ({
              value: type,
              label: t(`screens.cs.vehicle_manager.fuel_types.${type}`),
            }))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormText
            name="makeModel"
            label={t('screens.cs.vehicle_manager.add_edit_vehicle.make_model')}
            control={control}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormText
            name="assetId"
            label={t('screens.cs.vehicle_manager.add_edit_vehicle.asset_id')}
            control={control}
          />
        </Grid>
      </Grid>
    </KNDialog>
  )
}

export default CreateOrEditVehicleDialog
